import { useEffect } from 'react';
import type { Comic } from '@/mocks/comics';

interface Props {
  comic: Comic | null;
  followed: boolean;
  onFollow: (id: number) => void;
  onClose: () => void;
}

export default function ComicModal({ comic, followed, onFollow, onClose }: Props) {
  useEffect(() => {
    if (!comic) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [comic, onClose]);

  if (!comic) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={`《${comic.title}》详情`}>
      <button
        type="button"
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="关闭弹窗"
      ></button>

      <div className="relative z-10 grid w-full max-w-3xl animate-pop grid-cols-1 overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-lift sm:grid-cols-[280px_1fr] md:max-w-4xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-primary-100"
          aria-label="关闭"
        >
          <i className="ri-close-line text-xl" aria-hidden="true"></i>
        </button>

        <div className="relative aspect-[3/4] w-full overflow-hidden bg-background-100 sm:aspect-auto sm:h-full">
          <img src={comic.cover} alt={`${comic.title} 搞笑漫画封面`} className="h-full w-full object-cover object-top" />
          <span
            className={`absolute left-4 top-4 rounded-full px-3 py-1 text-sm text-background-50 ${
              comic.status === '连载中' ? 'bg-accent-500' : 'bg-foreground-700/80'
            }`}
          >
            {comic.status}
          </span>
        </div>

        <div className="flex flex-col p-5 md:p-7">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="rounded-full bg-primary-100 px-2.5 py-1 text-primary-700">{comic.categoryName}</span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-3-fill text-accent-500" aria-hidden="true"></i>
              {comic.likes.toLocaleString()} 人喜欢
            </span>
          </div>

          <h3 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">{comic.title}</h3>
          <p className="mt-1 text-sm text-foreground-600">作者：{comic.author} · 已更新至第 {comic.chapters} 话 · {comic.updateTime}</p>

          <p className="mt-4 text-sm leading-relaxed text-foreground-700">{comic.desc}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            {comic.tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                {t}
              </span>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={onFollow}
              className={`inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 py-3 text-sm font-medium transition-colors ${
                followed
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-200 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
              }`}
            >
              <i className={followed ? 'ri-star-fill' : 'ri-star-line'} aria-hidden="true"></i>
              {followed ? '已追更' : '追更'}
            </button>
            <a
              href="#comics"
              onClick={onClose}
              className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-circle-line" aria-hidden="true"></i>
              开始阅读
            </a>
          </div>

          <p className="mt-4 text-xs text-foreground-400">
            《{comic.title}》为哈哈漫画网搞笑漫画热门连载，支持电脑、平板与手机随时随地笑一笑。
          </p>
        </div>
      </div>
    </div>
  );
}